import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { Checkbox } from '@/core/Checkbox.atom';
import { DEBOUNCE } from '@/core/core.constants';
import { useDebounce } from '@/core/hooks/useDebounce.hook';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { useAssetLeafChildNames } from '@/itemFinder/itemFinder.queries';
import { itemIconClass } from '@/utilities/utilities';
import { faSpinner } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon, LoadingIndicator } from '@seeqdev/qomponents';
import type React from 'react';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { AssetLeafChildName } from '@/itemFinder/itemFinder.constants';

/**
 * The icon class to show for a name row, or undefined if it shouldn't show one. The backend returns the distinct
 * concrete item types (StoredSignal, CalculatedCondition, etc.) sharing each name — usually just one, but a name can
 * be shared by items of different types (e.g. a Signal and a Condition with the same name). Reuse the same
 * type-to-icon mapping as search results (itemIconClass in @/utilities/utilities); only show an icon when every
 * type sharing the name resolves to the same icon, otherwise show none rather than guess.
 */
const rowIconClass = (childName: AssetLeafChildName): string | undefined => {
  const icons = new Set(childName.types.map((type) => itemIconClass({ type })));
  return icons.size === 1 ? [...icons][0] : undefined;
};

export interface AssetLeafChildrenSelectIF extends ValidatingFormComponent<string[]> {
  component: 'AssetLeafChildrenSelectFormComponent';
  /** The selected asset whose leaf-descendant names should be listed. */
  asset: string;
  /** Item types to include when listing leaf-descendant names. */
  types: string[];
  /** Case-insensitive substring filter applied to names; debounced internally before querying. */
  filter: string;
  /** The names currently checked. */
  selectedNames: string[];
  /** When true, "Select all" is active (a dynamic descendant query); individual names are ignored. */
  selectAll: boolean;
  /**
   * When true, `selectedNames` is known to already contain every name matching `filter` (set by the parent after
   * a filtered "select all" bulk action fully materializes them from the backend). Lets the "select all" checkbox
   * render checked immediately even if this component's own paginated name list hasn't loaded every page yet.
   */
  allFilteredChildrenSelected: boolean;
  /** Message shown when there are no names to display and nothing is loading. */
  emptyMessage: string;
  /**
   * Called when the "Select all" checkbox is toggled. When switching from selectAll=true to false by unchecking
   * an individual item, `materializeAllExcept` contains that item's name, signaling the parent should load all
   * names and populate selectedNames with everything except that name.
   *
   * While a filter is active, the parent scopes this to the names currently matching the filter (selecting/
   * deselecting only those, and preserving whatever was already selected outside the filter) rather than the
   * global dynamic select-all — see itemFinder.searchFormElements.utilities.ts's onToggleSelectAll.
   */
  onToggleSelectAll: (selectAll: boolean, materializeAllExcept?: string) => void;
  onToggleName: (name: string, checked: boolean) => void;
}

export const AssetLeafChildrenSelectFormComponent = (props: AssetLeafChildrenSelectIF) => {
  const {
    name,
    testId = 'assetLeafChildrenSelect',
    asset,
    types,
    filter,
    selectedNames,
    selectAll,
    allFilteredChildrenSelected,
    emptyMessage,
    onToggleSelectAll,
    onToggleName,
    extraClassNames,
    validation,
    extendValidation,
  } = props;
  const { t } = useTranslation();
  const scrollParentRef = useRef<HTMLDivElement>(null);

  // Debounce the filter before it feeds the query key, so typing doesn't fire a request per keystroke
  // (the text input itself stays controlled by the caller and updates immediately).
  const [debouncedFilter, setDebouncedFilter] = useState(filter);
  const debouncedSetFilter = useDebounce((value: string) => setDebouncedFilter(value), DEBOUNCE.MEDIUM);
  useEffect(() => {
    debouncedSetFilter(filter);
  }, [filter, debouncedSetFilter]);

  const { childNames, isLoading, hasMore, loadMore } = useAssetLeafChildNames(asset, types, debouncedFilter);

  // While filtered, "Select all" is scoped to the filtered names (see onToggleSelectAll doc), so its checked state
  // should reflect that scope too. Two independent signals can prove that: every name matching the filter that's
  // been loaded so far (by this component's own paginated list) is selected AND there's nothing left for it to
  // load; or the parent reports allFilteredChildrenSelected, meaning it already fetched and selected the complete
  // filtered match set from the backend regardless of how much of this component's own list has rendered. The
  // latter is what lets the checkbox show checked immediately for a filter with more matches than fit on one page,
  // so a second click sends `false` instead of redundantly re-sending `true`. Unfiltered, it always reflects the
  // global selectAll flag.
  const isFiltered = !!debouncedFilter.trim();
  const allLoadedFilteredNamesSelected =
    childNames.length > 0 &&
    !hasMore &&
    !isLoading &&
    childNames.every((childName) => selectedNames.includes(childName.name));
  const selectAllChecked = selectAll || (isFiltered && (allFilteredChildrenSelected || allLoadedFilteredNamesSelected));

  // Default validation: invalid if nothing is selected (neither select-all nor individual names)
  const defaultValidation = (value: string[]) => _.isEmpty(value);
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const { errors, hasValidationErrors, modified, submitFailed } = useFormState({
    subscription: { errors: true, hasValidationErrors: true, modified: true, submitFailed: true },
  });
  const showError = _.has(errors, name) && hasValidationErrors && (modified?.[name] || submitFailed);

  const handleToggleName = (name: string, checked: boolean) => {
    // If "select all" is active and user unchecks an item, materialize the full selection minus this item
    if (selectAll && !checked) {
      onToggleSelectAll(false, name); // Signal parent to load all names and exclude this one
      return; // Parent will handle the materialization; don't call onToggleName here
    }
    // // If "select all" is active and user checks an item (shouldn't happen as it's already checked, but handle it)
    if (selectAll && checked) {
      onToggleSelectAll(false);
      return; // Parent handles clearing select-all; don't call onToggleName
    }
    onToggleName(name, checked);
  };

  // For validation: track a computed value - use a sentinel for select-all, or the selected names array
  const fieldValue = selectAll ? ['__ALL__'] : selectedNames;

  return (
    <Field<string[]> name={name} validate={appliedValidation} value={fieldValue}>
      {() => (
        <FormFieldWrapper
          testId={testId}
          showError={showError}
          wrapperClassNames={clsx('flexRowContainer flexFill', extraClassNames)}>
          <div id={name} data-testid={testId} className="flexRowContainer flexFill">
            <Checkbox
              id={`${testId}-selectAll`}
              isChecked={selectAllChecked}
              label={t('ITEM_FINDER.ASSET_LEAF_CHILDREN.SELECT_ALL')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onToggleSelectAll(event.target.checked)}
            />

            {isLoading && childNames.length === 0 && (
              <div
                className="flexColumnContainer tw:mt-sq-10 tw:items-center tw:justify-center"
                data-testid={`${testId}-loading`}>
                <LoadingIndicator size="md" />
              </div>
            )}

            {!isLoading && childNames.length === 0 && (
              <div className="tw:mt-sq-10 tw:italic" data-testid={`${testId}-empty`}>
                {emptyMessage}
              </div>
            )}

            {childNames.length > 0 && (
              <div
                ref={scrollParentRef}
                className="tw:max-h-sq-300 tw:w-full tw:overflow-y-auto"
                data-testid={`${testId}-scrollArea`}>
                <InfiniteScroll
                  pageStart={0}
                  hasMore={hasMore && !isLoading}
                  loadMore={loadMore}
                  useWindow={false}
                  initialLoad={false}
                  getScrollParent={() => scrollParentRef.current}
                  loader={
                    <div key="loader" className="text-center p5" data-testid={`${testId}-loadingMore`}>
                      <Icon icon={faSpinner} extraClassNames="fa-pulse" />
                    </div>
                  }>
                  <table className={clsx('table', 'table-condensed', 'tw:mb-0')} data-testid={`${testId}-table`}>
                    <tbody>
                      {_.map(childNames, (childName) => {
                        const icon = rowIconClass(childName);
                        return (
                          <tr key={childName.name} className="cursorPointer">
                            <td className="tw:p-0!">
                              <Checkbox
                                id={`${testId}-${encodeURIComponent(childName.name)}`}
                                isChecked={selectAll || selectedNames.includes(childName.name)}
                                label={
                                  <span className="flexColumnContainer tw:items-center">
                                    {icon && (
                                      <Icon
                                        icon={icon}
                                        extraClassNames="mr5"
                                        testId={`${testId}-${encodeURIComponent(childName.name)}-icon`}
                                      />
                                    )}
                                    {`${childName.name} (${childName.count})`}
                                  </span>
                                }
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  handleToggleName(childName.name, event.target.checked)
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </InfiniteScroll>
              </div>
            )}
          </div>
        </FormFieldWrapper>
      )}
    </Field>
  );
};
